import { H1 as _H1 } from "components/atoms/Heading";
import React from "react";
import { types, Text, Image, Repeater } from "react-bricks/frontend";
import styled from "styled-components";
import { restrictedBackgroundColorProp } from "../utils/backgroundColor";
import TextBlock from "../common/TextBlock";
import { Link } from "@components/atoms";
import Button from "../common/Button";
import { LogoProps } from "../common/Logo";
import { color } from "styles/colours";
import TrustpilotSectionSmall from "../sections/TrustpilotSectionSmall";
import { eyebrowColorProp } from "../utils/colorPickerProps";

export interface HeroLeftTextProps {
  title: types.TextValue;
  body: types.TextValue;
  showButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  showBody?: boolean;
  showTopImage?: boolean;
  topImageMaxWidth?: string;
  showLogoImage?: boolean;
  logoImages?: types.RepeaterItems<LogoProps>;
  showBottomImage?: boolean;
  bottomImageMaxWidth?: string;
  inverted?: boolean;
  showTitle?: boolean;
  bodyColor?: types.IColor;
  showTrustpilot?: boolean;
  showCheckList?: boolean;
  eyebrowColor: string;
  showEyebrow?: boolean;
}

const HeroLeftText: types.Brick<HeroLeftTextProps> = ({
  title,
  showButton,
  showBody,
  buttonLink = "",
  buttonText,
  showTopImage,
  topImageMaxWidth,
  showLogoImage,
  logoImages = [],
  showBottomImage,
  bottomImageMaxWidth,
  inverted,
  showTitle,
  bodyColor,
  showTrustpilot,
  showCheckList,
  showEyebrow,
  eyebrowColor,
}) => {
  return (
    <Container inverted={inverted}>
      {showLogoImage && (
        <LogoWrapper>
          <Repeater
            propName="logoImages"
            items={logoImages}
            renderItemWrapper={(item, i) => (
              <>
                {i > 0 && <Line inverted={inverted} />}
                {item}
              </>
            )}
          />
        </LogoWrapper>
      )}
      <Spacer height="2rem" />
      {showTopImage && (
        <ImageWrapper width={topImageMaxWidth || "100%"}>
          <Image
            propName="topImage"
            alt="topImage"
            placeholder={() => "https://dummyimage.com/600x200/f3f4f6/9ca3af&text=Select+Image"}
          />
        </ImageWrapper>
      )}
      <Spacer height="2rem" />
      {showEyebrow && (
        <Eyebrow color={eyebrowColor}>
          <Text
            propName="eyebrowText"
            placeholder="Eyebrow"
            renderBlock={({ children }) => <>{children}</>}
          />
        </Eyebrow>
      )}
      {showTitle && (
        <Text
          propName="title"
          placeholder="Title goes here"
          value={title}
          renderBlock={({ children }) => <H1>{children}</H1>}
        />
      )}
      {showBody && (
        <BodyWrapper color={bodyColor?.color}>
          <TextBlock propName={"body"} fontSize="1.6rem" lineHeight="1.9rem" />
          <Spacer height="1rem" />
        </BodyWrapper>
      )}
      {showCheckList && (
        <Repeater
          propName="checklistItems"
          renderWrapper={(items) => <TextBlockWrap>{items}</TextBlockWrap>}
        />
      )}

      {showBottomImage && (
        <ImageWrapper width={bottomImageMaxWidth || "100%"}>
          <Image
            propName="bottomImage"
            alt="bottomImage"
            placeholder={() => "https://dummyimage.com/600x200/f3f4f6/9ca3af&text=Select+Image"}
          />
        </ImageWrapper>
      )}

      <Spacer height="1.5em" />
      {showButton && buttonText && (
        <Button
          text={buttonText}
          variant={inverted ? "white" : "primary"}
          href={buttonLink || "#"}
        />
      )}
      {showTrustpilot && (
        <TrustPilotWrapper>
          <TrustpilotSectionSmall alignment="left" backgroundColor={{ color: "transparent" }} />
        </TrustPilotWrapper>
      )}
    </Container>
  );
};

HeroLeftText.schema = {
  // Unique brick name
  name: "hero-left-text",
  label: "Hero Left Text",
  hideFromAddMenu: true,
  repeaterItems: [
    {
      name: "logoImages",
      itemType: "logo",
      itemLabel: "Logo",
      min: 0,
      max: 3,
    },
    {
      name: "checklistItems",
      itemType: "checklist-item",
      itemLabel: "Checklist Item",
      min: 0,
      max: 8,
    },
  ],
  // Defaults when a new brick is added
  getDefaultProps: () => ({
    title: "Title to describe what the webinar is about to go here here here what",
    body: "Supporting copy here",
    logoImageWidth: 170,
    showTitle: true,
    showBody: true,
    showBottomImage: true,
    showTopImage: true,
    showLogoImage: true,
    logoImages: [
      {
        maxWidth: 170,
      },
    ],
    bodyColor: { color: color.B1100 },
    showCheckList: false,
    showEyebrow: false,
    eyebrowColor: color.S3100,
    eyebrowText: "Featured",
  }),

  // Sidebar Edit controls for props
  sideEditProps: [
    restrictedBackgroundColorProp,
    {
      name: "showLogoImage",
      label: "Show top logos?",
      type: types.SideEditPropType.Boolean,
    },
    {
      name: "showTopImage",
      label: "Show top image?",
      type: types.SideEditPropType.Boolean,
    },
    {
      name: "showEyebrow",
      label: "Show Eyebrow",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    eyebrowColorProp,
    {
      name: "showTitle",
      label: "Show title?",
      type: types.SideEditPropType.Boolean,
    },
    {
      name: "showBody",
      label: "Show body copy?",
      type: types.SideEditPropType.Boolean,
    },
    {
      name: "bodyColor",
      label: "Body Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      show: (props) => props.showBody,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { value: { color: color.B1100 }, label: "Black" },
          { value: { color: color.P100 }, label: "Pink" },
        ],
      },
    },
    {
      name: "showCheckList",
      label: "Show Checklist??",
      type: types.SideEditPropType.Boolean,
    },
    {
      name: "showBottomImage",
      label: "Show bottom image?",
      type: types.SideEditPropType.Boolean,
    },
    {
      name: "showButton",
      label: "CTA Button?",
      type: types.SideEditPropType.Boolean,
    },
    {
      name: "buttonText",
      label: "Button Text",
      type: types.SideEditPropType.Text,
      show: (props) => props.showButton,
    },
    {
      name: "buttonLink",
      label: "Button Link",
      type: types.SideEditPropType.Text,
    },
    {
      name: "showTrustpilot",
      label: "Show Trustpilot?",
      type: types.SideEditPropType.Boolean,
    },
  ],
};

export default HeroLeftText;

const ImageWrapper = styled.div<{ width?: string }>`
  max-width: ${(p) => (p.width ? `${p.width}` : "auto")};
  img {
    max-width: 100%;
    height: auto;
  }
`;

const Spacer = styled.div<{ height: string }>`
  height: ${(p) => p.height};
`;

const Container = styled.div<{ inverted?: boolean }>`
  width: 100%;
  ${(p) => (p.inverted ? `h1, p, span { color: white !important }` : ``)};
`;

const H1 = styled(_H1)`
  margin-bottom: 2rem;
`;

const BodyWrapper = styled.div<{
  color?: string;
}>`
  span {
    color: ${(p) => p.color || color.B1100};
    font-weight: 700;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const Line = styled.div<{ inverted: boolean }>`
  height: 5rem;
  border-left: ${(p) => (p.inverted ? "1px solid white" : `1px solid ${color.B240}`)};
`;

const TrustPilotWrapper = styled.div`
  margin-top: 1.5rem;
`;

const TextBlockWrap = styled.div`
  margin-top: 1rem;
  text-align: left;
`;

const Eyebrow = styled.div<{
  color: string;
}>`
  font-weight: bold;
  font-size: 0.875rem;
  line-height: 1rem;
  color: ${(p) => p.color};
  text-align: left;
`;
